<template>
  <div class="counselor-contact-form contact-form">
    <transition mode="out-in" name="contact-form__fade">
      <form
        method="post"
        v-if="!formSent"
        ref="contactForm"
        :name="formName"
        @submit.prevent="submitForm()"
      >
        <meeting-type
          class="form-row"
          v-if="!disableMeetingTypeContactForm"
          v-model:meeting-type="meetingType"
          :available-meeting-types="availableMeetingTypes"
          :request-type="requestType"
        />

        <transition name="contact-form__reveal-animation">
          <case-type-select
            v-if="showCaseTypeSelect"
            :options="options"
            :request-type="requestType"
            @update-case-type="updateCaseType"
          />
        </transition>

        <transition name="contact-form__reveal-animation">
          <contact-info
            v-if="showContactInfo"
            ref="contactInfo"
            v-model:name="name"
            v-model:email="email"
            v-model:phone="phone"
            v-model:message="message"
            :message-is-required="messageIsRequired"
            :request-type="requestType"
            @trigger-field-error="triggerFieldError"
          />
        </transition>

        <submit-section
          v-model:accept="accept"
          :accept-text="contactTexts.contactFormAcceptTextStripeConnect"
          :error="error"
          :has-errors="hasErrors"
          :is-disabled="isDisabled"
          :loading="contactFormIsLoading"
          :show-accept-text="showAcceptText"
          :submit-button-text="texts.submit"
        />
      </form>

      <form-success-message
        v-else
        :counselor-form="true"
        :name="name"
        :request-type="requestType"
      />
    </transition>

    <loader :loading="contactFormIsLoading || isLoading" />
  </div>
</template>

<script>
import Loader from 'Components/loader/Loader.vue';
import { useContactForm } from 'Composables/useContactForm';
import { useCityTermLocalStorage } from 'Main/composables/useCityTermLocalStorage';
import { useUser } from 'Main/composables/useUser';
import CaseTypeSelect from 'Main/contact-form/components/CaseTypeSelect.vue';
import ContactInfo from 'Main/contact-form/components/ContactInfo.vue';
import FormSuccessMessage from 'Main/contact-form/components/FormSuccessMessage.vue';
import MeetingType from 'Main/contact-form/components/MeetingType.vue';
import SubmitSection from 'Main/contact-form/components/SubmitSection.vue';
import {
  DEFAULT_ROLES,
  FIELD_ORDER,
  isTherapyRequest,
  MEETING_TYPE_KEYS,
  REQUEST_TYPES,
  ROLES,
} from 'Main/contact-form/constants';
import Helpers from 'Modules/Helpers';
import { mapMutations, mapState } from 'vuex';

const FORM_LOCATION = 'counselor-profile';

export default {

  beforeUnmount() {
    this.wrapperObserver?.disconnect();
  },

  components: {
    CaseTypeSelect,
    ContactInfo,
    FormSuccessMessage,
    Loader,
    MeetingType,
    SubmitSection,
  },

  computed: {
    ...mapState('cities', ['cities', 'cityLinks']),
    ...mapState('user', [
      'caseTypes',
      'schedule',
      'isLoading',
      'role',
      'locations',
    ]),

    additionalFormData() {
      return { formType: 'hero' };
    },

    availableMeetingTypes() {
      if (!this.isLoading && this.hasValidLocation) {
        // Decople array from state.haxxxxx
        const userSchedule = JSON.parse(JSON.stringify(this.schedule));
        if (!userSchedule || this.hasAllMeetingsSlots(userSchedule)) {
          return {
            [MEETING_TYPE_KEYS.VIDEO]: this.texts.videoMeeting,
            [MEETING_TYPE_KEYS.PHONE]: this.texts.phoneMeeting,
            [MEETING_TYPE_KEYS.AT_CLIENTS_HOME]: this.texts.meetingYourHome,
            [MEETING_TYPE_KEYS.AT_OUR_OFFICES]: this.texts.meetingOurOffice,
          };
        }
      }

      return {
        [MEETING_TYPE_KEYS.VIDEO]: this.texts.videoMeeting,
        [MEETING_TYPE_KEYS.PHONE]: this.texts.phoneMeeting,
      };
    },

    contactFormIsLoading() {
      return this.$store.state.contactForm.isLoading;
    },

    formName() {
      return `contact-form-${this.requestType}-advisor-page`;
    },

    hasValidLocation() {
      if (!this.cityTerm || this.cities?.length === 0) {
        return false;
      }

      return this.locations.some(
        (location) =>
          location.name === this.cityTerm.name ||
          location.name === this.parentCity
      );
    },

    isDisabled() {
      return !(this.showAcceptText && this.accept);
    },

    isLegal() {
      return this.requestType === REQUEST_TYPES.LAW;
    },

    isTherapy() {
      return isTherapyRequest(this.requestType);
    },

    messageIsRequired() {
      return this.isLegal;
    },

    missingFields() {
      const requiredFields = ['name', 'phone', 'accept', 'requestType'];

      if (this.showCaseTypeSelect) {
        requiredFields.push('caseType');

        if (this.caseType === 'Annat') {
          requiredFields.push('message');
        }
      }

      if (this.isTherapy) {
        requiredFields.push('price', 'email', 'meetingType');
      }

      return requiredFields
        .filter((field) => !this[field])
        .sort((a, b) => FIELD_ORDER.indexOf(a) - FIELD_ORDER.indexOf(b));
    },

    options() {
      if (!(this.caseTypes && this.showCaseTypeSelect)) {
        return [];
      }

      const typeToFilterOn = this.isTherapy ? 'THERAPY' : 'LEGAL';

      return this.caseTypes
        .filter(({ type }) => type === typeToFilterOn)
        .map((item) => ({ label: item.name, value: item.slug }));
    },

    requestType: {
      get() {
        return this.role;
      },
      set(newType) {
        this.setRole(newType);
      },
    },

    selectedCity() {
      if (!this.cityTerm) {
        return null;
      }

      return this.cityTerm.name;
    },

    showAcceptText() {
      if (this.isTherapy) {
        return this.showContactInfo;
      }
      return true;
    },

    showCaseTypeSelect() {
      return this.isLegal || this.isTherapy;
    },

    showContactInfo() {
      if (! this.isTherapy) {
        return true;
      }
      return this.showCaseTypeSelect;
    },
  },

  created() {
    this.maybeFetchUser(this.advisorId, this.schedule.length);
    this.maybeGetPresetDataFromUrlParams();
    this.getParentCity();
  },

  data() {
    const {
      advisorId,
      advisorEmail,
      pageTitle = '',
      defaultRole,
    } = this.$mountEl.dataset;

    return {
      advisorEmail,
      advisorId,
      contactTexts: theme_vars.contactTexts,
      contactVia: pageTitle,
      defaultRole,
      disableCitySelectContactForm: theme_vars.disableCitySelectContactForm,
      disableMeetingTypeContactForm: theme_vars.disableMeetingTypeContactForm,
      formSent: false,
      meetingType: theme_vars.disableMeetingTypeContactForm
        ? MEETING_TYPE_KEYS.VIDEO
        : '',
      orgNumber: null,
      parentCity: null,
      texts: theme_vars.counselorContactFormTexts,
      today: new Date(),
      validate: ['name', 'phone', 'email'],
      wrapperObserver: null,
    };
  },

  methods: {
    ...mapMutations('user', ['setRole', 'setAvailableMeetingTypesOther']),

    async getParentCity() {
      if (!this.cityTerm) {
        return null;
      }

      if (this.cities?.length === 0) {
        await this.$store.dispatch('cities/getCities', {
          search: this.cityTerm.name,
        });
      }

      const city = this.cities.find(
        (city) => city.label === this.cityTerm.name
      );

      this.parentCity = city ? city.parent : null;
    },

    maybeGetPresetDataFromUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);

      if (!urlParams.has('caseType') && !urlParams.has('orgNumber')) {
        return;
      }

      this.caseType = urlParams.get('caseType');
      this.orgNumber = urlParams.get('orgNumber');
    },

    async submitForm() {
      await this.sendInquiry({
        additionalFormData: this.additionalFormData,
        advisorEmail: this.advisorEmail,
        advisorId: this.advisorId,
        contactInfoRef: this.$refs.contactInfo,
        contactVia: this.contactVia,
        formName: this.formName,
        formSent: this.formSent,
        meetingType: this.meetingType,
        missingFields: this.missingFields,
        onFormSent: (val) => {
          this.formSent = val;
        },
        orgNumber: this.orgNumber,
        refs: this.$refs,
        requestType: this.requestType,
        rootEl: this.$mountEl,
        selectedCity: this.selectedCity,
        texts: this.contactTexts,
        validateRefs: this.validate,
      });
    },

    // The form sits on the page instead of sliding in, so the funnel's
    // "form opened" step is the moment it becomes visible: on load for a
    // counselor whose form is the active tab, on the role switch for one
    // whose payment form is. Once per page view, like a single slide-in.
    trackFormOpen() {
      const wrapper = this.$mountEl?.closest('.counselor-contact-form-wrapper');

      if (!wrapper || wrapper.classList.contains('active')) {
        Helpers.gtmContactFormOpenEvent(FORM_LOCATION);
        return;
      }

      this.wrapperObserver = new MutationObserver(() => {
        if (!wrapper.classList.contains('active')) {
          return;
        }

        this.wrapperObserver.disconnect();
        Helpers.gtmContactFormOpenEvent(FORM_LOCATION);
      });

      this.wrapperObserver.observe(wrapper, { attributeFilter: ['class'] });
    },

    updateCaseType(val = '') {
      this.caseType = val;
    },
  },

  mounted() {
    if (this.defaultRole && !this.role) {
      switch (this.defaultRole) {
        case DEFAULT_ROLES.LAW:
          this.setRole(ROLES.LAW);
          break;
        case DEFAULT_ROLES.THERAPY:
          this.setRole(ROLES.THERAPY);
          break;
        default:
          this.setRole(ROLES.FUNERAL);
          break;
      }
    }

    this.trackFormOpen();
  },
  name: 'CounselorContactForm',

  setup() {
    const { maybeFetchUser, getUpdatedSchedule } = useUser();
    const {
      cityTerm,
      saveCityTermToLocalStorage,
      getCityTermFromLocalStorage,
    } = useCityTermLocalStorage();
    const contactForm = useContactForm();
    return {
      cityTerm,
      getCityTermFromLocalStorage,
      getUpdatedSchedule,
      maybeFetchUser,
      saveCityTermToLocalStorage,
      ...contactForm,
    };
  },

  watch: {

    availableMeetingTypes(availableMeetingTypes) {
      this.setAvailableMeetingTypesOther(availableMeetingTypes);
    },
    async cityLinks() {
      this.cityTerm = this.getCityTermFromLocalStorage();
      this.parentCity = await this.getParentCity();
    },

    formSent(newVal) {
      if (newVal) {
        const headerHeight =
          document.documentElement.style.getPropertyValue('--header-height');

        const scrollY =
          this.$refs.contactForm.getBoundingClientRect().top +
          window.scrollY -
          headerHeight;

        window.scrollTo({ behavior: 'smooth', top: scrollY });
      }
    },
  },
};
</script>
